import { useQuery } from '@apollo/client';

import logger from 'js/app/loggerSingleton';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';

import type { AdminGlobalPartnerFragment } from 'bundles/admin-shared/__queries__/__generated__/adminGlobalPartnerFragment';
import type {
  GetPartnerByIdQuery,
  GetPartnerByIdQueryVariables,
} from 'bundles/admin-shared/__queries__/__generated__/getPartnerById';
import getPartnerById from 'bundles/admin-shared/__queries__/getPartnerById.graphql';
import type CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import {
  COURSERA_PLUS,
  COURSERA_PLUS_SUBSCRIPTION,
  COURSERA_TIER_LITE,
  CREDENTIAL_TRACK,
  CREDENTIAL_TRACK_SUBSCRIPTION_V2,
  SPECIALIZATION,
  SPECIALIZATION_PREPAID,
  SPECIALIZATION_SUBSCRIPTION,
  VERIFIED_CERTIFICATE,
} from 'bundles/payments-common/common/ProductType';
import type { DegreeInfo } from 'bundles/payments-common/hooks/useDegreeProgramInfo';
import { useDegreeProgramInfo } from 'bundles/payments-common/hooks/useDegreeProgramInfo';
import useGetCourseById from 'bundles/payments-common/hooks/useGetCourseById';
import type { Course } from 'bundles/payments-common/hooks/useGetCourseById';
import { useGetSpecializationById } from 'bundles/payments-common/hooks/useGetSpecializationById';
import type { SpecializationType } from 'bundles/payments-common/hooks/useGetSpecializationById';

export type PartnerType = AdminGlobalPartnerFragment | undefined | null;

type PropsFromProductNaptime = {
  course?: Course;
};

type PropsFromGraphql = {
  degreeProgram?: DegreeInfo;
  partner?: PartnerType;
  s12n?: SpecializationType;
};

type PropsFromProductNaptimeAndGraphql = PropsFromProductNaptime & PropsFromGraphql;

export type PropsFromUsePartner = PropsFromProductNaptimeAndGraphql & {
  shouldHavePartner?: boolean;
};

const getSpecializationOrCourseIdFromCart = (cart?: CartsV2) => {
  const { productType, productItemId } = cart ?? {};
  let shouldHavePartner = true;
  let s12nId;
  let courseId;
  if (!cart) {
    return {
      s12nId,
      courseId,
      shouldHavePartner: false,
    };
  }

  switch (productType) {
    case SPECIALIZATION:
      s12nId = productItemId;
      break;
    case SPECIALIZATION_PREPAID: {
      const s12nCartItem = cart.items.find((item) => item.cartItem.productType === SPECIALIZATION);
      s12nId = s12nCartItem?.cartItem?.productItemId;
      break;
    }
    case SPECIALIZATION_SUBSCRIPTION: {
      s12nId = cart.getSpecializationIdFromCartItem(cart.firstCartItem);
      break;
    }
    case COURSERA_PLUS: {
      const productId = cart.getCourseraPlusProductIdToEnroll();

      if (productId) {
        const [, _s12nId] = stringKeyToTuple(productId);
        s12nId = _s12nId;
      }
      break;
    }
    case VERIFIED_CERTIFICATE:
      courseId = productItemId;
      break;
    case CREDENTIAL_TRACK_SUBSCRIPTION_V2:
    case CREDENTIAL_TRACK:
      // See useDegreeProgramInfo hook usage in this file for handling of this case.
      break;
    case COURSERA_PLUS_SUBSCRIPTION:
    case COURSERA_TIER_LITE:
      const productId =
        productType === COURSERA_TIER_LITE
          ? cart.getCourseraLiteProductIdToEnroll()
          : cart.getCourseraPlusProductIdToEnroll();

      if (productId) {
        // I'm explicitly setting shouldHavePartner to false as a band aid fix for a bug in the ideal upgrade flow:
        // Currently, a s12n is not being used as ProductIdToEnroll for the Plus upgrade cart, instead a courseId
        // is being used in its place. That courseId is being treated here as a s12nId, and so it doesn't
        // return any data when we try to get s12n by ID.
        // A permanent solution will be implemented in: https://coursera.atlassian.net/browse/SUBSCRIP-2737
        shouldHavePartner = false;
        const [_productType, _productId] = productId && stringKeyToTuple(productId);
        if (_productType === VERIFIED_CERTIFICATE) {
          courseId = _productId;
        } else {
          s12nId = _productId;
        }
      } else {
        shouldHavePartner = false;
      }
      break;
    default:
      // We should never get here.
      shouldHavePartner = false;
      s12nId = undefined;
      courseId = undefined;
      logger.error(`Unknown product type: ${productType} for productItemId: ${productItemId}`);
      return {};
  }
  return {
    s12nId,
    courseId,
    shouldHavePartner,
  };
};

export const usePartner = (cart?: CartsV2) => {
  const { s12nId, courseId, shouldHavePartner } = getSpecializationOrCourseIdFromCart(cart);
  const { course } = useGetCourseById({
    courseId,
    skip: !cart || !courseId,
  });

  const skipUseDegreeProgramInfo = !(
    cart &&
    (cart.productType === CREDENTIAL_TRACK_SUBSCRIPTION_V2 || cart.productType === CREDENTIAL_TRACK)
  );
  const { degreeProgram } = useDegreeProgramInfo(cart, skipUseDegreeProgramInfo);
  const { s12n } = useGetSpecializationById({ s12nId });
  const partnerId = s12n?.partners?.[0]?.id || course?.partners?.[0]?.id || degreeProgram?.partnerIds?.[0];

  // TODO: Clean up this partner graphql call now that all product types are fetched using graphql.
  // Now we can just get the partner object as a property on the Graphql response object.
  const { data } = useQuery<GetPartnerByIdQuery, GetPartnerByIdQueryVariables>(getPartnerById, {
    variables: {
      id: `${partnerId}`,
    },
    context: { clientName: 'gatewayGql' },
    skip: !partnerId,
  });

  return {
    course,
    s12n,
    degreeProgram,
    partner: data?.Partner?.queryById,
    shouldHavePartner,
  };
};
